import { useState, useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import axios from "axios";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import { createPDF, brandedAutoTable, addFooter, addHeader } from "@/utils/pdfUtils";
import {
  getInterventionBand, getPriorityLever, getDevelopmentFocus, getDimensionLevelKey
} from "@/utils/consultingLogic";
import { QRCodeSVG } from "qrcode.react";
import { toPng } from "html-to-image";
import { 
  Zap, ArrowLeft, Calendar, MapPin, Users, Copy, Download, 
  FileSpreadsheet, FileText, Target, QrCode
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const APP_URL = process.env.REACT_APP_BACKEND_URL?.replace('/api', '') || window.location.origin;

export default function AdminEventDetail() {
  const { eventId } = useParams();
  const navigate = useNavigate();
  const [event, setEvent] = useState(null);
  const [responses, setResponses] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showQR, setShowQR] = useState(false);
  const qrRef = useRef(null);

  const getAuthHeaders = () => ({
    headers: { Authorization: `Bearer ${localStorage.getItem("admin_token")}` },
  });

  useEffect(() => {
    const token = localStorage.getItem("admin_token");
    if (!token) {
      navigate("/admin");
      return;
    }
    fetchData();
  }, [eventId, navigate]);

  const fetchData = async () => {
    try {
      const [eventRes, responsesRes] = await Promise.all([
        axios.get(`${API}/events/${eventId}`, getAuthHeaders()),
        axios.get(`${API}/events/${eventId}/responses`, getAuthHeaders()),
      ]);
      setEvent(eventRes.data);
      setResponses(responsesRes.data.responses || []);
      setSummary(responsesRes.data.summary);
    } catch (error) {
      if (error.response?.status === 401) {
        localStorage.removeItem("admin_token");
        navigate("/admin");
      }
      toast.error("Failed to fetch event data");
    } finally {
      setLoading(false);
    }
  };

  const copyEventCode = () => {
    const code = event.code;
    if (navigator.clipboard && window.isSecureContext) {
      navigator.clipboard.writeText(code).then(() => toast.success("Event code copied!")).catch(() => {
        fallbackCopy(code);
      });
    } else {
      fallbackCopy(code);
    }
  };
  const fallbackCopy = (code) => {
    const ta = document.createElement("textarea");
    ta.value = code;
    ta.style.position = "fixed";
    ta.style.opacity = "0";
    document.body.appendChild(ta);
    ta.select();
    document.execCommand("copy");
    document.body.removeChild(ta);
    toast.success("Event code copied!");
  };

  const downloadQRCode = async () => {
    if (!qrRef.current) return;
    try {
      const dataUrl = await toPng(qrRef.current, { 
        backgroundColor: '#ffffff',
        pixelRatio: 3 
      });
      const link = document.createElement('a');
      link.download = `TeamPower_QR_${event.code}.png`;
      link.href = dataUrl;
      link.click();
      toast.success("QR Code downloaded!");
    } catch (error) {
      toast.error("Failed to download QR code");
    }
  };

  const exportToExcel = () => {
    if (responses.length === 0) {
      toast.error("No responses to export");
      return;
    }

    const data = responses.map((r, i) => ({
      "#": i + 1,
      "TeamPower Index": r.teampower_index,
      "Capability Avg": r.capability_avg,
      "Capability Score": r.capability_score,
      "Collaboration Avg": r.collaboration_avg,
      "Collaboration Score": r.collaboration_score,
      "Energy Avg": r.energy_avg,
      "Energy Score": r.energy_score,
      "Submitted At": r.submitted_at,
    }));

    // Add summary row
    if (summary) {
      data.push({});
      data.push({
        "#": "SUMMARY",
        "TeamPower Index": summary.avg_teampower_index,
        "Capability Score": summary.avg_capability_score,
        "Collaboration Score": summary.avg_collaboration_score,
        "Energy Score": summary.avg_energy_score,
      });
    }

    const ws = XLSX.utils.json_to_sheet(data);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Responses");

    const excelBuffer = XLSX.write(wb, { bookType: "xlsx", type: "array" });
    const blob = new Blob([excelBuffer], { type: "application/octet-stream" });
    saveAs(blob, `${event.name.replace(/\s+/g, "_")}_responses.xlsx`);
    toast.success("Excel exported!");
  };

  const exportToPDF = () => {
    if (responses.length === 0) {
      toast.error("No responses to export");
      return;
    }

    const doc = createPDF();
    const cap = summary?.avg_capability_score || 0;
    const col = summary?.avg_collaboration_score || 0;
    const en = summary?.avg_energy_score || 0;
    const tpi = summary?.avg_teampower_index || 0;

    const interventionBand = getInterventionBand(tpi);
    const devFocus = getDevelopmentFocus(cap, col, en);
    const priority = getPriorityLever(cap, col, en);

    // Header with TeamPower logo
    const contentY = addHeader(doc);

    // Event info
    doc.setFontSize(12);
    doc.setTextColor(80, 80, 80);
    doc.text(event.name, 105, contentY + 2, { align: "center" });
    doc.setFontSize(10);
    doc.text(`${event.date || ""}  |  ${responses.length} participants`, 105, contentY + 9, { align: "center" });

    // Average TeamPower Index
    doc.setFontSize(16);
    doc.setTextColor(0, 0, 0);
    doc.text("Average TeamPower Index", 20, contentY + 25);

    doc.setFontSize(36);
    doc.setTextColor(236, 72, 153);
    doc.text(`${tpi}`, 20, contentY + 43);

    // Intervention Intensity
    doc.setFontSize(12);
    doc.setTextColor(100, 100, 100);
    doc.text(`Intervention Intensity: ${interventionBand.intensity}`, 20, contentY + 53);

    // Dimension Scores Table
    const outcomeLabels = {
      skillsFutureReadiness: "Skills & Future Readiness",
      strategyClarityExecution: "Strategy Clarity & Execution",
      accountabilityOwnership: "Accountability & Ownership",
      crossFunctionalCollaboration: "Cross-Functional Collaboration",
      leadershipCapability: "Leadership Capability",
      meetingEfficiencyTimeUse: "Meeting Efficiency & Time Use",
      communicationTransparency: "Communication & Transparency",
      engagementMotivation: "Engagement & Motivation",
      talentRetentionGrowth: "Talent Retention & Growth",
      changeReadinessResilience: "Change Readiness & Resilience"
    };
    const trainingLabels = {
      strategyExecution: "Strategy & Execution",
      personalEffectivenessProductivity: "Personal Effectiveness & Productivity",
      leadershipPeopleManagement: "Leadership & People Management",
      collaborationTeamDynamics: "Collaboration & Team Dynamics",
      innovationFutureSkills: "Innovation & Future Skills",
      changeResilience: "Change & Resilience"
    };
    const levelLabel = (dim, score) => getDimensionLevelKey(dim, score).replace(/([A-Z])/g, " $1").trim();

    const dimData = [
      ["Capability", `${cap}%`, levelLabel("capability", cap)],
      ["Collaboration", `${col}%`, levelLabel("collaboration", col)],
      ["Energy", `${en}%`, levelLabel("energy", en)]
    ];

    brandedAutoTable(doc, {
      startY: contentY + 63,
      head: [["Dimension", "Avg Score", "Level"]],
      body: dimData,
      theme: "striped",
      styles: { fontSize: 10 },
    });

    // Priority Lever
    let y = doc.lastAutoTable.finalY + 15;
    doc.setFontSize(14);
    doc.setTextColor(0, 0, 0);
    doc.text("Priority Focus Area", 20, y);
    doc.setFontSize(12);
    doc.setTextColor(236, 72, 153);
    doc.text(priority[0].dimension.charAt(0).toUpperCase() + priority[0].dimension.slice(1), 20, y + 10);

    // Development Focus
    y += 25;
    doc.setFontSize(14);
    doc.setTextColor(0, 0, 0);
    doc.text("Recommended Development Focus", 20, y);

    if (devFocus.isReset) {
      doc.setFontSize(10);
      doc.setTextColor(180, 0, 0);
      doc.text("RESET INTERVENTION REQUIRED", 20, y + 10);
    }

    // Teambuilding Outcomes
    const outY = y + (devFocus.isReset ? 20 : 12);
    doc.setFontSize(11);
    doc.setTextColor(0, 0, 0);
    doc.text("Teambuilding Outcomes:", 20, outY);
    doc.setFontSize(10);
    doc.setTextColor(80, 80, 80);
    devFocus.teambuildingOutcomes.forEach((key, i) => {
      doc.text(`\u2022 ${outcomeLabels[key] || key}`, 25, outY + 10 + (i * 7));
    });

    // Training Features
    const trainY = outY + 10 + (devFocus.teambuildingOutcomes.length * 7) + 10;
    doc.setFontSize(11);
    doc.setTextColor(0, 0, 0);
    doc.text("Training Features:", 20, trainY);
    doc.setFontSize(10);
    doc.setTextColor(80, 80, 80);
    devFocus.trainingFeatures.forEach((key, i) => {
      doc.text(`\u2022 ${trainingLabels[key] || key}`, 25, trainY + 10 + (i * 7));
    });

    // Individual Responses Table (page 2)
    doc.addPage();
    doc.setFontSize(14);
    doc.setTextColor(0, 0, 0);
    doc.text("Individual Responses", 14, 20);

    const tableData = responses.map((r, i) => [
      i + 1,
      r.teampower_index,
      r.capability_score,
      r.collaboration_score,
      r.energy_score,
    ]);

    brandedAutoTable(doc, {
      startY: 28,
      head: [["#", "TeamPower", "Capability", "Collaboration", "Energy"]],
      body: tableData,
      theme: "grid",
    });

    // Footer on both pages
    const pageCount = doc.internal.getNumberOfPages();
    for (let i = 1; i <= pageCount; i++) {
      doc.setPage(i);
      addFooter(doc);
    }

    doc.save(`${event.name.replace(/\s+/g, "_")}_report.pdf`);
    toast.success("PDF exported!");
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#09090B] flex items-center justify-center">
        <div className="w-12 h-12 border-4 border-orange-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!event) {
    return (
      <div className="min-h-screen bg-[#09090B] flex items-center justify-center text-white">
        <p>Event not found</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#09090B] text-white">
      {/* Header */}
      <header className="px-4 sm:px-6 py-4 border-b border-zinc-800">
        <div className="max-w-7xl mx-auto flex flex-wrap items-center justify-between gap-2">
          <div className="flex items-center gap-2 sm:gap-4">
            <Button
              variant="ghost"
              onClick={() => navigate("/admin/dashboard")}
              className="text-zinc-400 hover:text-white px-2 sm:px-4"
              data-testid="back-button"
            >
              <ArrowLeft className="w-4 h-4 sm:mr-2" />
              <span className="hidden sm:inline">Back</span>
            </Button>
            <div className="h-6 w-px bg-zinc-800 hidden sm:block" />
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-lg bg-orange-500 flex items-center justify-center">
                <Zap className="w-4 h-4" />
              </div>
              <span className="font-heading text-sm tracking-wide hidden sm:inline">TEAMPOWER</span>
            </div>
          </div>

          {/* Export Buttons */}
          <div className="flex gap-2">
            <Button
              variant="secondary"
              size="sm"
              onClick={exportToExcel}
              className="bg-zinc-800 hover:bg-zinc-700 text-white"
              data-testid="export-excel-button"
            >
              <FileSpreadsheet className="w-4 h-4 sm:mr-2" />
              <span className="hidden sm:inline">Excel</span>
            </Button>
            <Button
              variant="secondary"
              size="sm"
              onClick={exportToPDF}
              className="bg-zinc-800 hover:bg-zinc-700 text-white"
              data-testid="export-pdf-button"
            >
              <FileText className="w-4 h-4 sm:mr-2" />
              <span className="hidden sm:inline">PDF</span>
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-6 sm:py-8">
        {/* Event Info */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-6 mb-8"
        >
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
            <div>
              <h1 className="font-heading text-2xl md:text-3xl tracking-wide mb-2">{event.name}</h1>
              <div className="flex flex-wrap gap-4 text-sm text-zinc-400">
                <div className="flex items-center gap-2">
                  <Calendar className="w-4 h-4" />
                  {event.date}
                </div>
                {event.location && (
                  <div className="flex items-center gap-2">
                    <MapPin className="w-4 h-4" />
                    {event.location}
                  </div>
                )}
                <div className="flex items-center gap-2">
                  <Users className="w-4 h-4" />
                  {responses.length} responses
                </div>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <Button
                variant="secondary"
                onClick={() => setShowQR(!showQR)}
                className="bg-zinc-800 hover:bg-zinc-700 text-white"
                data-testid="qr-code-button"
              >
                <QrCode className="w-4 h-4 mr-2" />
                QR Code
              </Button>
              <button
                onClick={copyEventCode}
                className="flex items-center gap-2 px-4 py-2 bg-zinc-800 rounded-lg hover:bg-zinc-700 transition-colors"
                data-testid="copy-code-button"
              >
                <Copy className="w-4 h-4 text-orange-400" />
                <span className="font-mono text-lg tracking-widest">{event.code}</span>
              </button>
            </div>
          </div>
          
          {/* QR Code Display */}
          {showQR && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: "auto" }}
              exit={{ opacity: 0, height: 0 }}
              className="mt-6 pt-6 border-t border-zinc-800"
            >
              <div className="flex flex-col md:flex-row items-center gap-6">
                <div ref={qrRef} className="bg-white p-6 rounded-xl">
                  <QRCodeSVG 
                    value={`${APP_URL}/questionnaire/${event.code}`}
                    size={180}
                    level="H"
                    includeMargin={false}
                  />
                  <p className="text-center mt-3 text-zinc-800 font-mono font-bold text-xl">{event.code}</p>
                </div>
                <div className="text-center md:text-left flex-1">
                  <h3 className="font-heading text-xl mb-2">Scan to Participate</h3>
                  <p className="text-zinc-400 text-sm mb-3">
                    Display this QR code during your event for participants to quickly access the questionnaire.
                  </p>
                  <p className="text-orange-400 font-mono text-sm break-all">
                    {APP_URL}/questionnaire/{event.code}
                  </p>
                  <p className="text-zinc-500 text-sm mt-2">
                    Event Code: <span className="font-bold text-white">{event.code}</span>
                  </p>
                  <Button
                    onClick={downloadQRCode}
                    className="mt-4 bg-orange-500 hover:bg-orange-600"
                    data-testid="download-qr-button"
                  >
                    <Download className="w-4 h-4 mr-2" />
                    Download QR Code
                  </Button>
                </div>
              </div>
            </motion.div>
          )}
        </motion.div>

        {/* Summary Cards */}
        {summary && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-8"
          >
            <div className="bg-zinc-900/50 border border-orange-500/30 rounded-xl p-4 col-span-2 md:col-span-1">
              <p className="text-zinc-500 text-sm mb-1">TeamPower Index</p>
              <p className="text-3xl font-bold text-orange-400">{summary.avg_teampower_index}</p>
              <p className="text-xs text-zinc-500 mt-1">{summary.teampower_level}</p>
            </div>
            <div className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-4">
              <p className="text-zinc-500 text-sm mb-1">Capability</p>
              <p className="text-2xl font-bold text-blue-400">{summary.avg_capability_score}</p>
            </div>
            <div className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-4">
              <p className="text-zinc-500 text-sm mb-1">Collaboration</p>
              <p className="text-2xl font-bold text-purple-400">{summary.avg_collaboration_score}</p>
            </div>
            <div className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-4">
              <p className="text-zinc-500 text-sm mb-1">Energy</p>
              <p className="text-2xl font-bold text-orange-400">{summary.avg_energy_score}</p>
            </div>
            <div className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-4">
              <p className="text-zinc-500 text-sm mb-1">Responses</p>
              <p className="text-2xl font-bold text-white">{summary.total_responses}</p>
            </div>
          </motion.div>
        )}

        {/* Responses Table */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="bg-zinc-900/50 border border-zinc-800 rounded-xl overflow-hidden"
        >
          <div className="px-6 py-4 border-b border-zinc-800">
            <h2 className="font-heading text-lg">Individual Responses</h2>
          </div>

          {responses.length === 0 ? (
            <div className="p-12 text-center">
              <Users className="w-12 h-12 text-zinc-700 mx-auto mb-4" />
              <p className="text-zinc-400">No responses yet</p>
              <p className="text-zinc-500 text-sm mt-1">Share the event code with participants</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="data-table" data-testid="responses-table">
                <thead>
                  <tr>
                    <th>#</th>
                    <th>TeamPower Index</th>
                    <th>Capability</th>
                    <th>Collaboration</th>
                    <th>Energy</th>
                    <th>Submitted</th>
                  </tr>
                </thead>
                <tbody>
                  {responses.map((response, index) => (
                    <tr key={response.id} data-testid={`response-row-${index}`}>
                      <td className="text-zinc-500">{index + 1}</td>
                      <td>
                        <span className="text-orange-400 font-bold">{response.teampower_index}</span>
                      </td>
                      <td>
                        <span className="text-blue-400">{response.capability_score}</span>
                        <span className="text-zinc-600 text-xs ml-1">({response.capability_avg})</span>
                      </td>
                      <td>
                        <span className="text-purple-400">{response.collaboration_score}</span>
                        <span className="text-zinc-600 text-xs ml-1">({response.collaboration_avg})</span>
                      </td>
                      <td>
                        <span className="text-orange-400">{response.energy_score}</span>
                        <span className="text-zinc-600 text-xs ml-1">({response.energy_avg})</span>
                      </td>
                      <td className="text-zinc-500 text-sm">
                        {new Date(response.submitted_at).toLocaleDateString()}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </motion.div>
      </main>
    </div>
  );
}
